import { WorkAPI } from './API'
import { v4 } from 'uuid'
import {
  QuestionsQueryDTO,
  QuestionsResponseDTO,
  AnswerDTO,
  QuestionDTO,
  BoolQuestionDTO,
  TextQuestionDTO,
  NumericQuestionDTO,
  DateTimeQuestionDTO,
  DocumentsQuestionDTO,
  SelectQuestionDTO,
  BreakerQuestionDTO,
} from './dtos/questions'
import { Id } from 'types/definitions'
import { decimalToNumber, IDecimal, numberToDecimal } from 'utils/grpc-util'
import {
  ListJobQuestionsRequest,
  MakeCorrectionsRequest,
} from '@taxfyle/api-internal/internal/job_pb'
import {
  BoolInputValue,
  DateInputValue,
  DocumentsInputValue,
  InputValue,
  NumericInputValue,
  Question,
  SelectInputValue,
  TextInputValue,
} from '@taxfyle/api-internal/internal/question_input_value_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { ReplaceDraftQuestionAnswersRequest } from '@taxfyle/api-internal/internal/draft_pb'

const maybeDecimalToNumber = (value: IDecimal | undefined) => {
  return value ? decimalToNumber(value) : 0
}

const hasAnswer = (dto: AnswerDTO) =>
  dto.type &&
  dto.type !== 'BREAKER' &&
  dto.answer !== undefined &&
  dto.answer !== null

/**
 * Question store.
 */
export default class QuestionStore {
  constructor(private api: WorkAPI) {}

  /**
   * Fetches questions.
   *
   * @param query
   */
  fetchQuestions = async (
    query: QuestionsQueryDTO,
  ): Promise<QuestionsResponseDTO> => {
    return this._listQuestions(query.job, query.answers)
  }

  /**
   * Creates a session for a job ID.
   */
  createSessionForJob = (jobId: Id) => {
    return QuestionsQuerySession(Id.assert(jobId), this.fetchQuestions)
  }

  replaceQuestionAnswers = async (jobId: Id, answers: AnswerDTO[]) => {
    const request = new ReplaceDraftQuestionAnswersRequest().setId(
      Id.assert(jobId),
    )

    // If the input answers are empty, then it's an explicit "clear" and we don't
    // need to set anything.
    if (!answers.length) {
      request.getAnswersMap().clear()
    } else {
      const filtered = answers.filter(hasAnswer)

      // If filtered is empty, then it means we shouldn't save anything.
      // This could be due to missing type information which the server
      // cannot deal with. This happens due to `job.answers` not including
      // type information. Once Work API is dead, we can fix
      // the answers and make `type` required.
      if (!filtered.length) {
        return
      }

      filtered.forEach((question) => {
        request.getAnswersMap().set(question.id, QuestionMapper.toDTO(question))
      })
    }

    await this.api.drafts.replaceQuestionAnswers(request)
  }

  makeCorrections = async (jobId: Id, answers: AnswerDTO[], reason: string) => {
    const request = new MakeCorrectionsRequest()
      .setId(Id.assert(jobId))
      .setReason(reason)

    answers.filter(hasAnswer).forEach((question) => {
      request.getAnswersMap().set(question.id, QuestionMapper.toDTO(question))
    })

    await this.api.jobsV3.makeCorrections(request)
  }

  private _listQuestions = async (jobId: Id, answers: AnswerDTO[]) => {
    const request = new ListJobQuestionsRequest().setId(Id.assert(jobId))

    answers.filter(hasAnswer).forEach((question) => {
      request.getAnswersMap().set(question.id, QuestionMapper.toDTO(question))
    })

    return this.api.jobsV3.listQuestions(request).then((response) => ({
      questions: response.getQuestionsList().map(QuestionMapper.fromDTO),
      done: response.getIntakeCompleted(),
    }))
  }
}

/**
 * Using a session, we can optimize the speed at which
 * Work API computes questions. A session is preconfigured with a job.
 */
export interface QuestionsQuerySession {
  /**
   * Fetches questions.
   *
   * @param answers
   * @param includeVariables
   */
  fetchQuestions(
    answers: AnswerDTO[],
    includeVariables?: boolean,
  ): Promise<QuestionsQueryResponse>
}

/**
 * Creates a questions session.
 *
 * @param jobId
 * @param fetchQuestionsInternal
 */
export function QuestionsQuerySession(
  jobId: string,
  fetchQuestionsInternal: (
    query: QuestionsQueryDTO,
  ) => Promise<QuestionsResponseDTO>,
): QuestionsQuerySession {
  const sessionId = v4()
  return {
    fetchQuestions,
  }

  /**
   * Fetch questions.
   * @param answers
   * @param includeVariables
   */
  async function fetchQuestions(
    answers: AnswerDTO[],
  ): Promise<QuestionsQueryResponse> {
    const result = await fetchQuestionsInternal({
      answers: answers.filter((a) => a.answer !== undefined),
      session_id: sessionId,
      job: jobId,
    })

    return {
      done: result.done,
      questions: result.questions,
    }
  }
}

/**
 * No-op query session.
 */
QuestionsQuerySession.none = {
  fetchQuestions: async () => ({ done: true, questions: [], variables: [] }),
} as QuestionsQuerySession

/**
 * Questions query response.
 */
export interface QuestionsQueryResponse {
  /**
   * Whether we are done answering all questions.
   */
  done: boolean
  /**
   * The questions themselves.
   */
  questions: QuestionDTO[]
}

const QuestionMapper = {
  toDTO: (inputDTO: AnswerDTO) => {
    const inputValue = new InputValue()

    switch (inputDTO.type) {
      case 'BOOL': {
        inputValue.setBoolValue(new BoolInputValue().setValue(inputDTO.answer))
        break
      }
      case 'TEXT':
        inputValue.setTextValue(new TextInputValue().setValue(inputDTO.answer))
        break
      case 'NUMERIC':
        inputValue.setNumericValue(
          new NumericInputValue().setValue(numberToDecimal(inputDTO.answer)),
        )
        break
      case 'SELECT':
        if (Array.isArray(inputDTO.answer)) {
          inputValue.setSelectValue(
            new SelectInputValue().setMulti(
              new SelectInputValue.Multi().setSelectedIdsList(inputDTO.answer),
            ),
          )
        } else {
          inputValue.setSelectValue(
            new SelectInputValue().setSingle(
              new SelectInputValue.Single().setSelectedId(inputDTO.answer),
            ),
          )
        }
        break
      case 'DATE_TIME': {
        const value = new Timestamp()
        value.fromDate(inputDTO.answer)

        inputValue.setDateValue(new DateInputValue().setValue(value))
        break
      }
      case 'DOCUMENTS': {
        const documentsList = inputDTO.answer.map((answer: any) =>
          new DocumentsInputValue.DocumentsAnswerEntry()
            .setId(answer.id)
            .setLinkedDocumentId(answer.document_id),
        )
        inputValue.setDocumentsValue(
          new DocumentsInputValue().setSelectedDocumentsList(documentsList),
        )
        break
      }
      default:
        throw new Error('Question type is not supported.')
    }

    return inputValue
  },
  fromDTO: (inputValue: Question) => {
    switch (inputValue.getType()) {
      case 'Bool':
        return {
          id: inputValue.getId(),
          question: inputValue.getQuestion(),
          description: inputValue.getDescription()?.getValue(),
          type: 'BOOL',
          answer: inputValue.getAnswer()?.getBoolValue()?.getValue(),
        } satisfies BoolQuestionDTO
      case 'Text':
        return {
          id: inputValue.getId(),
          question: inputValue.getQuestion(),
          description: inputValue.getDescription()?.getValue(),
          type: 'TEXT',
          answer: inputValue.getAnswer()?.getTextValue()?.getValue(),
        } satisfies TextQuestionDTO
      case 'Numeric': {
        return {
          id: inputValue.getId(),
          question: inputValue.getQuestion(),
          description: inputValue.getDescription()?.getValue(),
          type: 'NUMERIC',
          answer: maybeDecimalToNumber(
            inputValue.getAnswer()?.getNumericValue()?.getValue(),
          ),
          format:
            inputValue.getFormat()?.getType() === 'Integer'
              ? { type: 'Integer' }
              : {
                  type: 'Decimal',
                  places: inputValue.getFormat()?.getPlaces(),
                },
        } satisfies NumericQuestionDTO
      }
      case 'DateTime':
        return {
          id: inputValue.getId(),
          question: inputValue.getQuestion(),
          description: inputValue.getDescription()?.getValue(),
          type: 'DATE_TIME',
          answer: inputValue.getAnswer()?.getDateValue()?.getValue()?.toDate(),
          collect_time: inputValue.getCollectTime(),
          disallow_future: inputValue.getDisallowFuture(),
          disallow_past: inputValue.getDisallowPast(),
        } satisfies DateTimeQuestionDTO
      case 'Select':
        return {
          id: inputValue.getId(),
          question: inputValue.getQuestion(),
          description: inputValue.getDescription()?.getValue(),
          type: 'SELECT',
          answer: inputValue.getMulti()?.getValue()
            ? inputValue
                .getAnswer()
                ?.getSelectValue()
                ?.getMulti()
                ?.getSelectedIdsList()
            : inputValue
                .getAnswer()
                ?.getSelectValue()
                ?.getSingle()
                ?.getSelectedId(),
          multi: inputValue.getMulti()?.getValue(),
          options: inputValue.getOptionsList().map((option) => ({
            id: option.getId(),
            text: option.getText(),
          })),
        } satisfies SelectQuestionDTO
      case 'Documents':
        return {
          id: inputValue.getId(),
          question: inputValue.getQuestion(),
          description: inputValue.getDescription()?.getValue(),
          type: 'DOCUMENTS',
          answer: inputValue
            .getAnswer()
            ?.getDocumentsValue()
            ?.getSelectedDocumentsList()
            .map((document) => {
              return {
                id: document.getId(),
                document_id: document.getLinkedDocumentId(),
              }
            }),
          options: inputValue.getOptionsList().map((option) => ({
            id: option.getId(),
            text: option.getText(),
          })),
        } satisfies DocumentsQuestionDTO
      case 'Breaker':
        return {
          id: inputValue.getId(),
          question: inputValue.getQuestion(),
          description: inputValue.getDescription()?.getValue(),
          type: 'BREAKER',
        } satisfies BreakerQuestionDTO
      default:
        throw new Error('Question type not supported')
    }
  },
}
